import axios from 'axios';
import { Api } from 'lib';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EntitySettings } from 'screens/Admin/components';
import { DataInfo } from './';
import { canAwardP4PDay } from './P4PDayGift';

const API_URL = process.env.REACT_APP_API_URL;

const Settings = ({
  user,
  verifyIdVisible,
  setVerifyIdVisible,
  onOpenP4PDay,
  setShowRemoveAlert
}) => {
  const [loading, setLoading] = useState(null),
    [visible, setVisible] = useState(false),
    currentUser = useSelector((state) => state.user),
    { t } = useTranslation(),
    dispatch = useDispatch();

  function handleVisible() {
    setVisible(!visible);
  }

  function handleVerifyId() {
    setVerifyIdVisible(!verifyIdVisible);
  }

  async function sendVerificationEmail() {
    try {
      await axios.post(`${API_URL}/admin/sendVerificationEmail`, { userId: user._id });

      dispatch({
        type: 'HANDLE_RES',
        payload: { res: 'Verification email sent.' }
      });
    } catch (e) {
      const message = e.response?.data
        ? e.response.data
        : 'Something went wrong.\nPlease try again later.';

      dispatch({
        type: 'HANDLE_RES',
        payload: { err: message }
      });
    }
  }

  async function toggleBan() {
    setLoading('ban');

    const res = await Api.post('/admin/toggleBan', { userId: user._id });
    setLoading(null);
    if (!res) return null;

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: res.banned ? 'User banned.' : 'User unbanned.' }
    });

    dispatch({
      type: 'ADMIN_EDIT_USER',
      payload: {
        ...user,
        ...res
      }
    });
  }

  async function unlockUser() {
    setLoading('unlock');

    const res = await Api.post('/admin/unlockUser', { userId: user._id });
    setLoading(null);
    if (!res) return null;

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: 'User login unlocked.' }
    });

    dispatch({
      type: 'ADMIN_EDIT_USER',
      payload: {
        ...user,
        ...res
      }
    });
  }

  async function toggleAdminAccess() {
    setLoading('admin');

    const res = await Api.post('/admin/toggleAdminAccess', { userId: user._id });
    setLoading(null);
    if (!res) return null;

    dispatch({
      type: 'HANDLE_RES',
      payload: { res: res.admin ? 'Granted admin access.' : 'Revoked admin access.' }
    });

    dispatch({
      type: 'ADMIN_EDIT_USER',
      payload: {
        ...user,
        ...res
      }
    });
  }

  if (!user) return null;

  const settings = [
    {
      text: 'Verify user',
      icon: 'profileOutline',
      onClick: handleVerifyId
    },
    {
      text: 'Resend verification email',
      icon: 'emailNotification',
      onClick: sendVerificationEmail
    },
    {
      text: user.banned ? 'Unban user' : 'Ban user',
      icon: user.banned ? 'check' : 'closeCircled',
      onClick: toggleBan,
      isDisabled: loading === 'ban'
    },
    {
      text: 'Unlock login',
      icon: 'check',
      onClick: unlockUser,
      isDisabled: loading === 'unlock',
      shouldHide: !user.loginAttempts
    },
    {
      text: user.admin ? 'Revoke admin access' : 'Grant admin access',
      icon: user.admin ? 'logout' : 'factCheck',
      onClick: toggleAdminAccess,
      isDisabled: loading === 'admin'
    },
    {
      text: 'Edit data info',
      icon: 'settings',
      onClick: handleVisible,
      isDisabled: loading === 'dataInfo',
      dontCloseOnClick: true,
      shouldHide: currentUser.email !== 'ceyhuncarpar@gmail.com'
    },
    {
      text: 'P4P Day gift',
      icon: 'starFilled',
      onClick: onOpenP4PDay,
      // Only fans who actually enrolled in the campaign — this is meaningless for anyone else,
      // and the entry disappears on its own once the campaign is over and nobody new enrols.
      shouldHide: canAwardP4PDay(user)
    },
    {
      // Same modal, opened in its award mode: a fan the QR never reached (or who only uses the
      // web) can be handed a badge here, spending the same stock an app enrolment would.
      text: t('admin.p4pDay.award.menuItem'),
      icon: 'starFilled',
      onClick: onOpenP4PDay,
      shouldHide: !canAwardP4PDay(user)
    },
    {
      text: 'Remove user',
      icon: 'delete',
      onClick: () => setShowRemoveAlert(true),
      isDisabled: loading === 'remove'
    }
  ];

  return (
    <React.Fragment>
      <EntitySettings
        user={user}
        options={settings}
        ExtraComponent={() => <DataInfo visible={visible} close={handleVisible} user={user} />}
      />
    </React.Fragment>
  );
};

export default Settings;
